<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0" class="app-sidebar">
    <div class="sidebar-layout">
      <VaAccordion v-model="value" multiple>
        <VaCollapse v-for="(route, index) in visibleRoutes" :key="index">
          <template #header="{ value: isCollapsed }">
            <VaSidebarItem
              :to="route.children ? undefined : { name: route.name }"
              :active="routeHasActiveChild(route)"
              :active-color="activeColor"
              :text-color="textColor(route)"
              :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
              role="button"
              class="sidebar-item"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-4 flex items-center justify-between w-full">
                <div class="flex items-center gap-3 flex-1">
                  <VaIcon
                    v-if="route.meta.icon"
                    aria-hidden="true"
                    :name="route.meta.icon"
                    size="20px"
                    :color="iconColor(route)"
                    class="icon-transition"
                  />
                  <VaSidebarItemTitle class="leading-5 font-semibold text-sm">
                    {{ t(route.displayName) }}
                  </VaSidebarItemTitle>
                </div>
                <div class="flex items-center gap-2">
                  <span v-if="getBadgeCount(route.name)" class="badge">
                    {{ getBadgeCount(route.name) }}
                  </span>
                  <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="18px" class="arrow-icon" />
                </div>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </template>
          <template #body>
            <div v-for="(childRoute, index2) in route.children" :key="index2">
              <VaSidebarItem
                :to="{ name: childRoute.name }"
                :active="isActiveChildRoute(childRoute)"
                :active-color="activeColor"
                :text-color="textColor(childRoute)"
                :aria-label="`Visit ${t(route.displayName)}`"
                class="sidebar-item-child"
              >
                <VaSidebarItemContent class="py-3 pr-2 pl-11">
                  <VaSidebarItemTitle class="leading-5 font-semibold text-sm">
                    {{ t(childRoute.displayName) }}
                  </VaSidebarItemTitle>
                </VaSidebarItemContent>
              </VaSidebarItem>
            </div>
          </template>
        </VaCollapse>
      </VaAccordion>

      <!-- <div v-if="!mobile" class="sidebar-bottom-visual" aria-hidden="true">
        <img src="/logo.png" alt="Suivi depenses" class="sidebar-bottom-image" />
      </div> -->
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useProjectsStore } from '../../stores/projects'
import { useHemocultureAuthStore } from '../../stores/hemoculture-auth'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const { t } = useI18n()
    const projectsStore = useProjectsStore()
    const authStore = useHemocultureAuthStore()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`)
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`))
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))
    const isAdminUser = computed(() => authStore.isAdmin)

    const visibleRoutes = computed(() =>
      navigationRoutes.routes.filter((navigationRoute) => {
        return !navigationRoute.meta.adminOnly || isAdminUser.value
      }),
    )

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    const getBadgeCount = (routeName: string) => {
      if (routeName === 'projects' && projectsStore.items.length > 0) {
        return projectsStore.items.length
      }
      return null
    }

    watch(
      () => [route.fullPath, visibleRoutes.value.length],
      () => {
        value.value = visibleRoutes.value.map((navigationRoute: INavigationRoute) =>
          routeHasActiveChild(navigationRoute),
        )
      },
      { immediate: true },
    )

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      visibleRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      getBadgeCount,
    }
  },
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  background: var(--va-background-secondary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  :deep(.va-sidebar) {
    background: transparent;
    height: 100%;
  }

  :deep(.va-accordion) {
    gap: 0.5rem;
    padding: 0.75rem 0;
  }
}

.sidebar-layout {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sidebar-bottom-visual {
  margin-top: auto;
  padding: 0.9rem 0.95rem 1rem;
}

.sidebar-bottom-image {
  width: 100%;
  max-height: 170px;
  object-fit: contain;
  border-radius: 14px;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f6ff 100%);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  padding: 0.5rem;
}

.sidebar-item,
.sidebar-item-child {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  :deep(.va-sidebar-item__content) {
    border-radius: 0.5rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background-color: rgba(var(--va-primary-rgb), 0.08);
      transform: translateX(2px);
    }
  }

  &.va-sidebar-item--active {
    :deep(.va-sidebar-item__content) {
      background-color: rgba(var(--va-primary-rgb), 0.12);
      border-left: 3px solid var(--va-primary);
      padding-left: calc(1rem - 3px);
      box-shadow: inset 0 0 8px rgba(var(--va-primary-rgb), 0.08);
    }
  }
}

.sidebar-item-child {
  :deep(.va-sidebar-item__content) {
    margin-left: 0.25rem;

    &:hover {
      background-color: rgba(var(--va-primary-rgb), 0.06);
    }
  }

  &.va-sidebar-item--active {
    :deep(.va-sidebar-item__content) {
      background-color: rgba(var(--va-primary-rgb), 0.08);
      border-left: 2px solid var(--va-primary);
      padding-left: calc(2.75rem - 2px);
    }
  }
}

.icon-transition {
  transition: all 0.3s ease;
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 0.5rem;
  background-color: var(--va-primary);
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: badge-pulse 2s ease-in-out infinite;

  @keyframes badge-pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
}
</style>
